import { getServiceUrl, httpGet, httpPost } from '../services/api';
import { logger } from '../services/logger';
import { RaceResults } from '@staycool/lvdc-types/race-results';
import { BetHistoryFilter, GetBetsHistoryResponse } from '@staycool/lvdc-types/bet-history';
import { TicketReceipt } from '@staycool/lvdc-types/bets';
import { getStoreValue } from '../stores/store/utils';
import { environment } from '../stores/environment/environment';
import { GetTrackRaceReplayFilter, GetTrackRaceReplayResponse, LivestreamDetails, RacePoolTotal } from './lvdc/types';
import { getCurrentDateString } from '../services/lvdc/utils';
import { TrackWithRaceInfo, FollowingRace, RaceDetails } from '@staycool/lvdc-types/track-v-2';
import { RaceCarryover } from '@staycool/lvdc-types/race-v-2';
import { isProd } from '../services/environment';
import { storageGet } from '../services/storage';
import moment from 'moment';
import uniqBy from 'lodash/uniqBy';

const getUrl = (url) => getServiceUrl('lvdc-web', url);

export async function loadTodaysTracks(): Promise<TrackWithRaceInfo[]> {
    try {
        const url = getUrl(`tracks`);
        const queryProps: { date: string; associationId?: number } = { date: getCurrentDateString() };
        const todayTracksInfo = await httpGet<TrackWithRaceInfo[]>(url, queryProps);

        if (!isProd() && storageGet('development.horse-racing-dev-mode')) {
            return todayTracksInfo.map((trackInfo) => {
                trackInfo.timestamp_to_post = moment(trackInfo.timestamp_to_post).date(moment().date()).toISOString();
                return trackInfo;
            });
        }

        return uniqBy(todayTracksInfo, 'nevada_track_id');
    } catch (error) {
        logger.error('LvdcWebMicroservice', 'loadTodaysTracks', error);
        return [];
    }
}

export async function loadAdvanceWageringTracks() {
    try {
        const url = getUrl(`tracks/advance-wagering-tracks`);
        const queryProps: { date: string; associationId?: number } = { date: getCurrentDateString() };
        return await httpGet<TrackWithRaceInfo[]>(url, queryProps);
    } catch (error) {
        logger.error('LvdcWebMicroservice', 'loadAdvanceWageringTracks', error);
        return [];
    }
}

export async function loadTrackRaceDetails(nevadaTrackId: number, raceId: number, date: string): Promise<RaceDetails> {
    try {
        const url = getUrl(`tracks/details`);
        const queryProps: { date: string; nevadaTrackId: number; raceId: number; associationId?: number } = {
            date,
            nevadaTrackId,
            raceId,
        };

        const trackDetails = await httpGet<RaceDetails>(url, queryProps);
        if (!isProd() && storageGet('development.horse-racing-dev-mode')) {
            return {
                ...trackDetails,
                betTypes: trackDetails.betTypes.map((betType) => {
                    return {
                        ...betType,
                        is_betting_enabled: true,
                    };
                }),
                raceInfo: {
                    ...trackDetails.raceInfo,
                    timestamp_to_post: moment(trackDetails.raceInfo.timestamp_to_post)
                        .date(moment().date())
                        .toISOString(),
                    current_race: 1,
                    track_races: trackDetails.raceInfo.track_races.map((race) => {
                        race.timestamp_to_post = moment(race.timestamp_to_post).date(moment().date()).toISOString();
                        race.is_ended = false;
                        race.is_betting_enabled = true;
                        return race;
                    }),
                    is_track_completed: false,
                    is_ended: false,
                },
            } as any;
        }
        return trackDetails;
    } catch (error) {
        logger.error('LvdcWebMicroservice', 'loadTrackRaceDetails', error);
        return {} as RaceDetails;
    }
}

export async function getBetHistory(filter: BetHistoryFilter) {
    try {
        const url = getUrl(`bets-history`);
        return await httpPost<GetBetsHistoryResponse>(url, filter);
    } catch (error) {
        logger.error('LvdcWebMicroservice', 'getBetHistory', error);
        return null;
    }
}

export async function getFollowingRaces(
    nevadaTrackId: number | undefined,
    raceIds: number[],
    date: string,
): Promise<FollowingRace[]> {
    if (!nevadaTrackId) {
        return [];
    }
    try {
        const url = getUrl('tracks/following-races');
        return await httpPost<FollowingRace[]>(url, { date, nevadaTrackId, raceIds });
    } catch (error) {
        logger.error('LvdcWebMicroservice', 'getFollowingRaces', error);
        return [];
    }
}

export async function placeHorseRacingBet(betPlacingData) {
    const url = getUrl(`bets`);

    return httpPost<BetPlacementResponse>(url, {
        ...betPlacingData,
    });
}

export async function getTicketReceipt(ticketId: number) {
    const url = getUrl(`ticket/receipt/${ticketId}`);
    return httpGet<TicketReceipt>(url);
}

export async function loadCarryovers() {
    try {
        const url = getUrl(`race/v2/${getCurrentDateString()}/carryovers/`);
        return await httpGet<RaceCarryover[]>(url);
    } catch (error) {
        logger.error('LvdcWebMicroservice', 'loadCarryovers', error);
        return [];
    }
}

export async function getResults(nevadaTrackId: number, raceId: number, date?: string) {
    try {
        const url = getUrl(`race-results/${date ?? getCurrentDateString()}/${nevadaTrackId}/${raceId || 1}/`);
        return await httpGet<RaceResults>(url);
    } catch (error) {
        logger.error('LvdcWebMicroservice', 'getResults', error);
    }
}

export async function getTrackRaceReplayUrl(filter: GetTrackRaceReplayFilter) {
    try {
        const { RCN_REFERER } = getStoreValue(environment);
        const url = getUrl(`replay/fetch-url`);
        return await httpPost<GetTrackRaceReplayResponse>(url, { ...filter, RCN_REFERER });
    } catch (error) {
        logger.error('LvdcWebMicroservice', 'getTrackRaceReplayUrl', error);
        return null;
    }
}

export async function getRacePoolTotals(nevadaTrackId: number, raceId: number, date: string) {
    try {
        const url = getUrl(`/race-betting-pool/total/${date}/${nevadaTrackId}/${raceId}`);
        return await httpGet<RacePoolTotal[]>(url);
    } catch (error) {
        logger.error('LvdcWebMicroservice', 'getRacePoolTotals', error);
        return [];
    }
}

export async function getLivestreamDetails(nevadaTrackId: number, raceId: number) {
    try {
        const url = getUrl(`tracks/livestream-info`);
        const { livestream } = await httpGet<{ livestream: LivestreamDetails }>(url, {
            date: getCurrentDateString(),
            nevadaTrackId,
            raceId,
        });
        return livestream;
    } catch (error) {
        logger.error('LvdcWebMicroservice', 'getLivestreamDetails', error);
        return { code: null, is_stream_available: false };
    }
}
interface BetPlacementResponse {
    ticket_id: number;
}
